import MatterIndicators, { hasMatterAccess } from 'components/matters/MatterIndicators';
import { Tooltip } from 'components/ui/tooltip';
import React from 'react';
import { MatterDto, MatterListItemMin, MatterVisibilityInfo } from 'types/matters';
import { cn } from './cn';
import { t } from './getTranslations.ts';

type RenderOptionProps = React.HTMLAttributes<HTMLElement>;

/** A non-selectable option, greyed. */
const DISABLED_OPTION_CLASS = 'bg-grey-200 cursor-not-allowed opacity-70';

export const hasLimitedVisibility = (option: MatterVisibilityInfo) => !hasMatterAccess(option);

export const isConfidentialMatter = (option: MatterVisibilityInfo) => option.isConfidential === true;

const isDisabledOption = (option: MatterVisibilityInfo) => hasLimitedVisibility(option);

const isClientPersonaNonGrata = (option: MatterListItemMin) => option.clientParty?.isPersonaNonGrata === true;

/** Case blocking, as the case header reads it — ALWAYS_BLOCKED or a currently blocked client. */
const isMatterBlocked = (option: MatterListItemMin) => option.isCurrentlyBlocked === true;

type MatterOptionProps = MatterListItemMin & { showBlocked?: boolean; blockedTooltipKey?: string };

/**
 * Renders a matter option with its confidentiality, access and blocking indicators leading the name.
 * Options with LIMITED visibility are non-selectable (handled by DynamicSelectInput) — the icons
 * only describe the case, they never decide what is selectable.
 */
const MatterOptionComponent = React.memo(({ showBlocked, blockedTooltipKey, ...option }: MatterOptionProps) => (
    <div className="flex w-full items-center gap-2">
        <MatterIndicators
            isConfidential={option.isConfidential}
            hasAccess={hasMatterAccess(option)}
            isBlocked={showBlocked}
            blockedTooltipKey={blockedTooltipKey}
            variant="option"
        />
        <div className="flex-1">{option.fullName}</div>
    </div>
));

MatterOptionComponent.displayName = 'MatterOptionComponent';

/**
 * The confidentiality and access indicators alone, for a filter row or a picker's selected value.
 * Null for a case carrying neither, so a selected-value overlay only replaces the plain input text
 * when there is something to lead it with. A value prefilled from `MatterDto` spells no-access as
 * `isRestricted` rather than `visibility`, so both are read.
 */
export const renderMatterIndicators = (option: MatterVisibilityInfo & Pick<MatterDto, 'isRestricted'>) => {
    const hasAccess = hasMatterAccess(option) && option.isRestricted !== true;
    return isConfidentialMatter(option) || !hasAccess ? (
        <MatterIndicators isConfidential={option.isConfidential} hasAccess={hasAccess} variant="option" />
    ) : null;
};

/**
 * Renders a matter option row with its visibility indicators, and whether it is selectable
 */
export const renderMatterOption = (
    option: MatterListItemMin,
    props: RenderOptionProps,
    getValue: (option: MatterListItemMin) => string | number | null
) => {
    const isDisabled = isDisabledOption(option);
    const originalOnClick = props.onClick;

    const handleClick = (e: React.MouseEvent<HTMLElement>) => {
        if (isDisabled) {
            e.preventDefault();
            e.stopPropagation();
        } else if (originalOnClick) {
            originalOnClick(e);
        }
    };

    const element = (
        <div
            {...props}
            key={getValue(option)}
            onClick={handleClick}
            className={cn(props.className, isDisabled ? DISABLED_OPTION_CLASS : 'cursor-pointer')}
        >
            <MatterOptionComponent {...option} />
        </div>
    );

    return { element, isDisabled };
};

/**
 * Renders a matter option row with its visibility indicators, and whether it is selectable —
 * restricted matters conditionally, based on permission
 * @param canSelectRestricted - If true, allows selection of restricted matters; if false, restricted matters are visible but not selectable
 * @param blockedTooltipKey - Opt-in for the pickers whose write is gated on case blocking: a blocked
 * case is listed greyed out and non-selectable, with the case view's blocked icon in front of its
 * name and this key as the row tooltip — it names what the block stops, timecards or expenses.
 * Omitted elsewhere: correspondence and invoicing are not gated on it.
 */
export const renderMatterOptionWithRestrictedSelection = <TOption extends MatterListItemMin>(
    option: TOption,
    props: RenderOptionProps,
    getValue: (option: TOption) => string | number | null,
    canSelectRestricted: boolean = true,
    blockedTooltipKey?: string
) => {
    const isLimitedVisibility = !canSelectRestricted && hasLimitedVisibility(option);
    const isPersonaNonGrata = isClientPersonaNonGrata(option);
    const isBlocked = blockedTooltipKey !== undefined && isMatterBlocked(option);
    const isDisabled = isLimitedVisibility || isPersonaNonGrata || isBlocked;
    const originalOnClick = props.onClick;

    const handleClick = (e: React.MouseEvent<HTMLElement>) => {
        if (isDisabled) {
            e.preventDefault();
            e.stopPropagation();
        } else if (originalOnClick) {
            originalOnClick(e);
        }
    };

    let tooltipText = '';
    if (isPersonaNonGrata) {
        tooltipText = t('contacts.tooltips.add-new-case-disabled-persona-non-grata');
    } else if (isBlocked) {
        tooltipText = t(blockedTooltipKey);
    }

    const row = (
        <div
            {...props}
            key={getValue(option)}
            onClick={handleClick}
            className={cn(props.className, isDisabled ? DISABLED_OPTION_CLASS : 'cursor-pointer')}
        >
            <MatterOptionComponent {...option} showBlocked={isBlocked} blockedTooltipKey={blockedTooltipKey} />
        </div>
    );

    const element = tooltipText ? (
        <Tooltip content={tooltipText} key={getValue(option)}>
            <span className="block">{row}</span>
        </Tooltip>
    ) : (
        row
    );

    return { element, isDisabled };
};
